import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation, NetworkStatus } from '@apollo/client';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatPhoneForAPI } from '../utils/phoneUtils';
import { DEFAULTS } from '../config';
import 'react-phone-number-input/style.css';
import './PhoneInput.css';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const GET_CONTACTS = gql`
  query GetContacts($page: Int, $pageSize: Int, $search: String, $groupId: Int, $isBlocked: Boolean, $isFavourite: Boolean) {
    contacts(page: $page, pageSize: $pageSize, search: $search, groupId: $groupId, isBlocked: $isBlocked, isFavourite: $isFavourite) {
      contacts {
        id
        name
        email
        phone
        notes
        isFavourite
        isBlocked
        unsubscribedWhatsapp
        unsubscribedEmail
        createdAt
        updatedAt
        groups {
          id
          name
        }
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

const GET_USER_SUBSCRIPTION = gql`
  query GetUserSubscription {
    mySubscriptions {
      id
      status
      starts_at
      expires_at
      package {
        id
        name
        max_staff_members
        max_contacts
        max_whatsapp_instances
        max_templates
        max_bulk_messages_per_day
        duration_days
        package_type
        price
        currency
      }
    }
  }
`;

const ADD_CONTACT = gql`
  mutation AddContact($input: AddContactInput!) {
    addContact(input: $input) {
      id
      name
      email
      phone
      notes
      isFavourite
      isBlocked
      createdAt
    }
  }
`;

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: Int!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      name
      email
      phone
      notes
      isFavourite
      isBlocked
      updatedAt
    }
  }
`;

const DELETE_CONTACT = gql`
  mutation DeleteContact($id: Int!) {
    deleteContact(id: $id)
  }
`;

const ContactManagement = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, contactId: null, contactName: '' });
  const [filterBlocked, setFilterBlocked] = useState('all');
  const [filterFavourite, setFilterFavourite] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [contactsPerPage] = useState(10);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Debounce search term to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Reset page when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, searchTerm]);

  // Form data for Create/Edit Contact
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    isFavourite: false
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    isFavourite: false
  });

  // Get user subscription for limits checking
  const { data: subscriptionData } = useQuery(GET_USER_SUBSCRIPTION, {
    skip: currentUser?.role !== 'user_admin',
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Get contacts with better loading state management
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_CONTACTS, {
    variables: {
      page: currentPage,
      pageSize: contactsPerPage,
      search: debouncedSearchTerm || undefined,
      isBlocked: filterBlocked === 'all' ? undefined : filterBlocked === 'blocked',
      isFavourite: filterFavourite === 'all' ? undefined : filterFavourite === 'favourite'
    },
    fetchPolicy: 'network-only', // Always fetch fresh data
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true, // Enable loading states for refetch
  });

  const [addContact] = useMutation(ADD_CONTACT, {
    onCompleted: (data) => {
      console.log('✅ Contact added successfully:', data);
    },
    onError: (error) => {
      console.error('❌ Error adding contact:', error);
    }
  });

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    onCompleted: (data) => {
      console.log('✅ Contact updated successfully:', data);
    },
    onError: (error) => {
      console.error('❌ Error updating contact:', error);
    }
  });

  const [deleteContact] = useMutation(DELETE_CONTACT, {
    onCompleted: (data) => {
      console.log('✅ Contact deleted successfully:', data);
    },
    onError: (error) => {
      console.error('❌ Error deleting contact:', error);
    }
  });

  // Helper functions for package restrictions
  const getCurrentSubscription = () => {
    if (!subscriptionData?.mySubscriptions) return null;
    return subscriptionData.mySubscriptions.find(sub => sub.status === 'active');
  };

  const getContactLimits = () => {
    const subscription = getCurrentSubscription();
    if (!subscription) return { current: 0, max: 0, packageName: 'No Package' };

    const currentContactCount = data?.contacts?.total || 0;
    const maxContacts = subscription.package?.max_contacts || 0;
    const packageName = subscription.package?.name || 'Unknown Package';

    return { current: currentContactCount, max: maxContacts, packageName };
  };

  const canAddContact = () => {
    const limits = getContactLimits();
    return limits.current < limits.max;
  };

  // Validate phone number
  const validatePhone = (phone) => {
    if (!phone) return false;
    return isValidPhoneNumber(phone);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
      isFavourite: false
    });
    setShowCreateForm(false);
    setEditingContact(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check contact limits
    if (!canAddContact()) {
      setErrorMessage('Contact limit reached. Please upgrade your package to add more contacts.');
      setShowErrorModal(true);
      return;
    }

    // Validate phone number
    if (!validatePhone(formData.phone)) {
      setErrorMessage('Please enter a valid phone number.');
      setShowErrorModal(true);
      return;
    }

    try {
      const formattedPhone = formatPhoneForAPI(formData.phone);
      const contactData = {
        ...formData,
        phone: formattedPhone,
      };

      const { data: result } = await addContact({
        variables: { input: contactData },
        update(cache, { data }) {
          if (data?.addContact) {
            // Update cache to add the new contact
            try {
              const existingContacts = cache.readQuery({ 
                query: GET_CONTACTS,
                variables: getCacheVariables()
              });
              
              if (existingContacts) {
                cache.writeQuery({
                  query: GET_CONTACTS,
                  variables: getCacheVariables(),
                  data: {
                    contacts: {
                      ...existingContacts.contacts,
                      contacts: [data.addContact, ...existingContacts.contacts.contacts],
                      total: existingContacts.contacts.total + 1
                    }
                  }
                });
              }
            } catch (cacheError) {
              console.warn('⚠️ Cache update failed, will refetch:', cacheError);
              refetch();
            }
          }
        }
      });

      if (result?.addContact) {
        resetForm();
        setSuccessMessage(`Contact "${result.addContact.name}" has been added successfully.`);
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
      setErrorMessage(err.message || 'Failed to add contact. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setEditFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      notes: contact.notes || '',
      isFavourite: contact.isFavourite || false
    });
    setShowEditForm(true);
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number if changed
    if (editFormData.phone && !validatePhone(editFormData.phone)) {
      setErrorMessage('Please enter a valid phone number.');
      setShowErrorModal(true);
      return;
    }

    try {
      const updateData = { ...editFormData };
      if (editFormData.phone) {
        updateData.phone = formatPhoneForAPI(editFormData.phone);
      }

      const { data: result } = await updateContact({
        variables: {
          id: editingContact.id,
          input: updateData
        },
        update(cache, { data }) {
          if (data?.updateContact) {
            // Update cache
            try {
              const existingContacts = cache.readQuery({ 
                query: GET_CONTACTS,
                variables: getCacheVariables()
              });
              
              if (existingContacts) {
                cache.writeQuery({
                  query: GET_CONTACTS,
                  variables: getCacheVariables(),
                  data: {
                    contacts: {
                      ...existingContacts.contacts,
                      contacts: existingContacts.contacts.contacts.map(contact =>
                        contact.id === editingContact.id ? data.updateContact : contact
                      )
                    }
                  }
                });
              }
            } catch (cacheError) {
              console.warn('⚠️ Cache update failed, will refetch:', cacheError);
              refetch();
            }
          }
        }
      });

      if (result?.updateContact) {
        setShowEditForm(false);
        setEditingContact(null);
        setEditFormData({});
        setSuccessMessage(`Contact "${result.updateContact.name}" has been updated successfully.`);
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error updating contact:', err);
      setErrorMessage(err.message || 'Failed to update contact. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleDelete = (contactId, contactName) => {
    setDeleteConfirm({ show: true, contactId, contactName });
  };

  const confirmDelete = async () => {
    if (isDeleting) return;
    
    const contactIdToDelete = deleteConfirm.contactId;
    const contactNameToDelete = deleteConfirm.contactName;
    
    setIsDeleting(true);
    
    try {
      await deleteContact({ 
        variables: { id: contactIdToDelete },
        update(cache, { data }) {
          if (data?.deleteContact) {
            try {
              const existingContacts = cache.readQuery({ 
                query: GET_CONTACTS,
                variables: getCacheVariables()
              });
              
              if (existingContacts) {
                cache.writeQuery({
                  query: GET_CONTACTS,
                  variables: getCacheVariables(),
                  data: {
                    contacts: {
                      ...existingContacts.contacts,
                      contacts: existingContacts.contacts.contacts.filter(contact => contact.id !== contactIdToDelete),
                      total: existingContacts.contacts.total - 1
                    }
                  }
                });
              }
            } catch (cacheError) {
              console.warn('⚠️ Cache update failed, will refetch:', cacheError);
              refetch();
            }
          }
        }
      });
      
      setDeleteConfirm({ show: false, contactId: null, contactName: '' });
      setSuccessMessage(`Contact "${contactNameToDelete}" has been deleted successfully.`);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('❌ Error deleting contact:', err);
      setDeleteConfirm({ show: false, contactId: null, contactName: '' });
      setErrorMessage(err.message || 'Failed to delete contact. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const getCacheVariables = () => ({
    page: currentPage,
    pageSize: contactsPerPage,
    search: debouncedSearchTerm || undefined,
    isBlocked: filterBlocked === 'all' ? undefined : filterBlocked === 'blocked',
    isFavourite: filterFavourite === 'all' ? undefined : filterFavourite === 'favourite'
  });

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (err) {
      console.error('Error refreshing contacts:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Determine different loading states
  const isInitialLoading = loading && !data && networkStatus === NetworkStatus.loading;
  const isDataRefreshing = loading && data && networkStatus === NetworkStatus.refetch;
  const isSearching = loading && data && (networkStatus === NetworkStatus.setVariables);
  const isTypingSearch = searchTerm !== debouncedSearchTerm; // User is typing but debounce hasn't triggered yet
  const isDataOperationInProgress = isDataRefreshing || isSearching || isRefreshing || isTypingSearch;
  
  // Only show full loading screen on initial load (no data at all)
  if (isInitialLoading) return <div className="p-8 text-center text-gray-600">Loading contacts...</div>;
  if (error && !data) return <div className="p-8 text-center text-red-600">Error: {error.message}</div>;

  const contacts = data?.contacts?.contacts || [];
  const totalContacts = data?.contacts?.total || 0;
  const totalPages = data?.contacts?.totalPages || 0;
  const contactLimits = getContactLimits();

  return (
    <div className="p-4 md:p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6 overflow-hidden relative">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 relative z-10">
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                className={`flex-1 sm:flex-none justify-center text-white font-normal px-4 py-2.5 rounded-xl transition-all duration-300 shadow-md active:scale-95 flex items-center gap-2 text-[10px] uppercase tracking-wider ${
                  canAddContact() 
                    ? 'bg-bulky-blue-500 hover:bg-bulky-blue-600 shadow-bulky-blue-500/10' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                onClick={() => canAddContact() ? setShowCreateForm(true) : setShowLimitsModal(true)}
                disabled={!canAddContact()}
              >
                <span className="material-icons-round text-sm">person_add</span>
                <span>Add Contact</span>
              </button>
            </div>

            <div className="flex gap-2">
              <button
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 font-normal px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 text-[9px] uppercase tracking-wider"
                onClick={() => setShowLimitsModal(true)}
              >
                <span className="material-icons-round text-sm">info</span>
                <span>Limits</span>
              </button>

              <button
                className="bg-gray-50 hover:bg-gray-100 text-gray-500 font-normal px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 disabled:opacity-50 text-[9px] uppercase tracking-wider"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <span className={`material-icons-round text-sm ${isRefreshing ? 'animate-spin' : ''}`}>refresh</span>
                <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-6 bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-900 p-3 px-5 rounded-xl border border-white/5 shadow-inner">
            <div className="flex flex-col">
              <span className="text-[7px] font-normal text-bulky-navy-300 uppercase tracking-wider leading-none mb-1">Total Contacts</span>
              <span className="text-sm font-normal text-white leading-none tabular-nums">{totalContacts}</span>
            </div>
            <div className="w-px h-6 bg-white/10"></div>
            <div className="flex flex-col">
              <span className="text-[7px] font-normal text-bulky-navy-300 uppercase tracking-wider leading-none mb-1">Available</span>
              <span className="text-sm font-normal text-emerald-400 leading-none tabular-nums">{contactLimits.max - contactLimits.current}</span>
            </div>
            <div className="w-px h-6 bg-white/10"></div>
            <div className="flex flex-col">
              <span className="text-[7px] font-normal text-bulky-navy-300 uppercase tracking-wider leading-none mb-1">Usage</span>
              <span className="text-sm font-normal text-blue-400 leading-none tabular-nums">{Math.round((contactLimits.current / contactLimits.max) * 100)}%</span>
            </div>
          </div>
        </div>

        {/* Controls Row */}
        <div className="mt-5 pt-5 border-t border-gray-50 flex flex-col sm:flex-row gap-3">
          <div className="relative group flex-1 sm:w-48">
            <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 pointer-events-none text-lg">filter_list</span>
            <select
              value={filterBlocked}
              onChange={(e) => {
                setFilterBlocked(e.target.value);
                setCurrentPage(1);
              }}
              className="form-select pl-12 h-[46px]"
              disabled={isDataOperationInProgress}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="blocked">Blocked Only</option>
            </select>
            <span className="material-icons-round absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-base">expand_more</span>
            {isDataOperationInProgress && (
              <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          <div className="relative group flex-1 sm:w-48">
            <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-yellow-500 pointer-events-none text-lg">star</span>
            <select
              value={filterFavourite}
              onChange={(e) => {
                setFilterFavourite(e.target.value);
                setCurrentPage(1);
              }}
              className="form-select pl-12 h-[46px]"
              disabled={isDataOperationInProgress}
            >
              <option value="all">All Contacts</option>
              <option value="favourite">Favourites Only</option>
              <option value="regular">Regular Only</option>
            </select>
            <span className="material-icons-round absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-base">expand_more</span>
            {isDataOperationInProgress && (
              <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          <div className="relative group flex-[2]">
            <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors z-10 text-xl">search</span>
            <input
              type="text"
              placeholder="Search contacts (name, phone, email)..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="form-input pl-12 h-[46px]"
              disabled={isDataOperationInProgress}
            />
            {searchTerm && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isDataOperationInProgress}
              >
                <span className="material-icons-round text-lg">clear</span>
              </button>
            )}
            {isTypingSearch && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            {isDataOperationInProgress && searchTerm && !isTypingSearch && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
        {/* Loading Overlay for Data Operations - Only shows during data refresh, not initial load */}
        {isDataOperationInProgress && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-600 font-medium">
                {isTypingSearch ? 'Preparing search...' : isSearching ? 'Searching contacts...' : isRefreshing ? 'Refreshing data...' : 'Updating contacts...'}
              </p>
            </div>
          </div>
        )}

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100/50 border-b border-gray-200 text-[12px] font-bold text-bulky-navy-800 uppercase tracking-widest">
                <th className="px-8 py-6 text-left">Contact Info</th>
                <th className="px-8 py-6 text-left">Communication</th>
                <th className="px-8 py-6 text-left">Status</th>
                <th className="px-8 py-6 text-left">Added</th>
                <th className="px-8 py-6 text-right pr-12">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {contacts.map(contact => (
                <tr key={contact.id} className={`group hover:bg-bulky-blue-50/30 transition-all duration-300 ${contact.isBlocked ? 'bg-red-50/50' : ''}`}>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-bulky-navy-900 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg border-2 border-white group-hover:scale-105 transition-transform relative">
                        {contact.name?.substring(0, 2).toUpperCase() || 'CO'}
                        {contact.isFavourite && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                            <span className="material-icons-round text-white text-xs">star</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[14px] font-bold text-bulky-navy-900 tracking-tight leading-none mb-2 truncate">
                          {contact.name}
                        </p>
                        <p className="text-[11px] font-bold text-gray-400 tracking-widest uppercase opacity-70">ID: {contact.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-lg bg-green-50 flex items-center justify-center">
                          <span className="material-icons-round text-sm text-green-500">phone</span>
                        </div>
                        <p className="text-[13px] font-semibold text-bulky-navy-800 tracking-tight">{contact.phone}</p>
                      </div>
                      {contact.email && (
                        <div className="flex items-center gap-2.5">
                          <div className="w-6 h-6 rounded-lg bg-bulky-blue-50 flex items-center justify-center">
                            <span className="material-icons-round text-sm text-bulky-blue-500">alternate_email</span>
                          </div>
                          <p className="text-[11px] font-bold text-gray-400 tracking-tight truncate">{contact.email}</p>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2">
                      <div className={`inline-flex items-center gap-2.5 px-4 py-1.5 rounded-xl border-2 ${contact.isBlocked
                        ? 'bg-red-50/50 text-red-600 border-red-100'
                        : 'bg-emerald-50/50 text-emerald-600 border-emerald-100 shadow-[0_0_15px_rgba(16,185,129,0.1)]'
                        }`}>
                        <div className={`w-2 h-2 rounded-full ${contact.isBlocked ? 'bg-red-500' : 'bg-emerald-500 animate-pulse-slow shadow-[0_0_10px_rgba(16,185,129,0.6)]'}`}></div>
                        <span className="text-[11px] font-bold uppercase tracking-widest">{contact.isBlocked ? 'Blocked' : 'Active'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col gap-1">
                      <p className="text-[13px] font-bold text-bulky-navy-900 tracking-tight">
                        {new Date(contact.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-[10px] font-bold text-blue-500/60 tracking-widest uppercase">Contact Added</p>
                    </div>
                  </td>
                  <td className="px-6 py-2.5 text-right pr-6">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleEdit(contact)}
                        className="w-10 h-10 bg-white border border-gray-200 hover:border-bulky-blue-500 hover:text-bulky-blue-500 rounded-xl flex items-center justify-center transition-all active:scale-90 shadow-sm"
                        title="Edit Contact"
                      >
                        <span className="material-icons-round text-lg">edit</span>
                      </button>

                      <button
                        onClick={() => handleDelete(contact.id, contact.name)}
                        className="w-10 h-10 bg-white border border-gray-200 hover:border-red-500 hover:text-red-500 rounded-xl flex items-center justify-center transition-all active:scale-90 shadow-sm"
                        title="Delete Contact"
                      >
                        <span className="material-icons-round text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-gray-50">
          {contacts.map(contact => (
            <div key={contact.id} className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-bulky-navy-900 flex items-center justify-center text-white font-normal text-[9px] uppercase tracking-tighter shadow-sm relative">
                    {contact.name?.substring(0, 2).toUpperCase() || 'CO'}
                    {contact.isFavourite && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full flex items-center justify-center">
                        <span className="material-icons-round text-white text-[8px]">star</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-tight leading-none mb-1">{contact.name}</p>
                    <p className="text-[7px] font-normal text-gray-400 uppercase tracking-wider">ID: {contact.id}</p>
                  </div>
                </div>
                <div className={`px-2 py-0.5 rounded border ${contact.isBlocked ? 'bg-red-50 text-red-600 border-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                  <span className="text-[7px] font-normal uppercase tracking-wider">{contact.isBlocked ? 'BLOCKED' : 'ACTIVE'}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 bg-gray-50/50 p-2.5 rounded-lg border border-gray-100">
                <div>
                  <p className="text-[7px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Phone</p>
                  <p className="text-[9px] font-normal text-bulky-navy-600">{contact.phone}</p>
                </div>
                {contact.email && (
                  <div>
                    <p className="text-[7px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Email</p>
                    <p className="text-[9px] font-normal text-bulky-navy-600 truncate">{contact.email}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-1.5 pt-1">
                <button onClick={() => handleEdit(contact)} className="flex-1 bg-white border border-gray-200 text-bulky-navy-400 py-2 rounded-md flex items-center justify-center active:scale-95 transition-all text-xs">
                  <span className="material-icons-round text-sm">edit</span>
                </button>
                <button onClick={() => handleDelete(contact.id, contact.name)} className="flex-1 bg-white border border-gray-200 text-red-500 hover:bg-red-50 py-2 rounded-md flex items-center justify-center active:scale-95 transition-all text-xs">
                  <span className="material-icons-round text-sm">delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-50 bg-gray-50/30 relative">
            {isDataOperationInProgress && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] opacity-80">
              <span>Displaying Contacts</span>
              <span className="text-bulky-navy-900">{((currentPage - 1) * contactsPerPage) + 1}</span>
              <span className="opacity-30">/</span>
              <span className="text-bulky-navy-900">{Math.min(currentPage * contactsPerPage, totalContacts)}</span>
              <span className="opacity-30">of</span>
              <span className="text-bulky-navy-900">{totalContacts}</span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || isDataOperationInProgress}
                className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-100 text-gray-400 hover:text-bulky-blue-500 disabled:opacity-30 transition-all shadow-sm"
              >
                <span className="material-icons-round text-sm">chevron_left</span>
              </button>

              <div className="flex items-center gap-1 mx-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    disabled={isDataOperationInProgress}
                    className={`w-7 h-7 flex items-center justify-center text-[10px] font-normal rounded-lg transition-all disabled:opacity-50 ${currentPage === page
                      ? 'bg-bulky-navy-900 text-white shadow-md'
                      : 'bg-white text-gray-400 border border-gray-50 hover:border-gray-200'
                      }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || isDataOperationInProgress}
                className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-100 text-gray-400 hover:text-bulky-blue-500 disabled:opacity-30 transition-all shadow-sm"
              >
                <span className="material-icons-round text-sm">chevron_right</span>
              </button>
            </div>
          </div>
        )}

        {contacts.length === 0 && (
          <div className="p-20 text-center bg-gray-50/50">
            <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 text-gray-300">
              <span className="material-icons-round text-4xl">contacts</span>
            </div>
            <p className="text-bulky-navy-300 font-normal tracking-tight uppercase text-xs">No contacts found in your directory.</p>
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      {showCreateForm && createPortal(
        <div className="modal-overlay">
          <div className="modal-container max-w-2xl">
            <div className="modal-header bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                  <span className="material-icons-round text-2xl">person_add</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white tracking-tight leading-none">Add New Contact</h2>
                  <p className="text-bulky-navy-300 text-[11px] font-semibold tracking-wider mt-1.5 opacity-80 uppercase">Expanding your network directory</p>
                </div>
              </div>
              <button onClick={resetForm} className="w-11 h-11 hover:bg-white/10 text-white rounded-2xl flex items-center justify-center transition-all bg-white/5 border border-white/5 shadow-lg">
                <span className="material-icons-round text-2xl">close</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
              <div className="modal-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="space-y-1.5">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="form-input"
                        placeholder="Enter full name..."
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="form-label">Phone Number *</label>
                      <PhoneInput
                        international
                        defaultCountry={DEFAULTS.COUNTRY}
                        value={formData.phone}
                        onChange={(v) => setFormData({ ...formData, phone: v || '' })}
                        className="phone-input tech-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-1.5">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="form-input"
                        placeholder="Enter email address..."
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="form-label">Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="form-input min-h-[80px] resize-none"
                        placeholder="Add any notes about this contact..."
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="favourite"
                        checked={formData.isFavourite}
                        onChange={(e) => setFormData({ ...formData, isFavourite: e.target.checked })}
                        className="w-4 h-4 text-yellow-500 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
                      />
                      <label htmlFor="favourite" className="form-label mb-0">Mark as Favourite</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Add Contact</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Contact Modal */}
      {showEditForm && editingContact && createPortal(
        <div className="modal-overlay">
          <div className="modal-container max-w-2xl">
            <div className="modal-header bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                  <span className="material-icons-round text-2xl">edit</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white tracking-tight leading-none">Edit Contact</h2>
                  <p className="text-bulky-navy-300 text-[11px] font-semibold tracking-wider mt-1.5 opacity-80 uppercase">Updating: {editingContact.name}</p>
                </div>
              </div>
              <button onClick={() => setShowEditForm(false)} className="w-11 h-11 hover:bg-white/10 text-white rounded-2xl flex items-center justify-center transition-all bg-white/5 border border-white/5 shadow-lg">
                <span className="material-icons-round text-2xl">close</span>
              </button>
            </div>

            <form onSubmit={handleEditFormSubmit} className="flex flex-col overflow-hidden">
              <div className="modal-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="space-y-1.5">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        className="form-input"
                        placeholder="Enter full name..."
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="form-label">Phone Number *</label>
                      <PhoneInput
                        international
                        defaultCountry={DEFAULTS.COUNTRY}
                        value={editFormData.phone}
                        onChange={(v) => setEditFormData({ ...editFormData, phone: v || '' })}
                        className="phone-input tech-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-1.5">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        className="form-input"
                        placeholder="Enter email address..."
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="form-label">Notes</label>
                      <textarea
                        value={editFormData.notes}
                        onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                        className="form-input min-h-[80px] resize-none"
                        placeholder="Add any notes about this contact..."
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="editFavourite"
                        checked={editFormData.isFavourite}
                        onChange={(e) => setEditFormData({ ...editFormData, isFavourite: e.target.checked })}
                        className="w-4 h-4 text-yellow-500 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
                      />
                      <label htmlFor="editFavourite" className="form-label mb-0">Mark as Favourite</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setShowEditForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Update Contact</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && createPortal(
        <div className="modal-overlay">
          <div className="modal-container max-w-sm">
            <div className="modal-header bg-red-600 from-red-600 to-red-700">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-white text-sm">warning</span>
                <h2 className="font-medium">Confirm Delete</h2>
              </div>
              <button onClick={() => setDeleteConfirm({ show: false, contactId: null, contactName: '' })} className="text-white hover:bg-white/20 p-1 rounded">
                <span className="material-icons-round text-sm">close</span>
              </button>
            </div>
            <div className="modal-content text-center py-6">
              <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteConfirm.contactName}</strong>?</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-2">This operation is irreversible</p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setDeleteConfirm({ show: false, contactId: null, contactName: '' })} className="btn-secondary">Cancel</button>
              <button 
                onClick={confirmDelete} 
                disabled={isDeleting}
                className={`btn-danger ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isDeleting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </div>
                ) : (
                  'Delete Contact'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Limits Modal */}
      {showLimitsModal && createPortal(
        <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md z-[999999] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-white/20 animate-in zoom-in duration-300">
            <div className="bg-bulky-navy-800 p-6 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-white">
                  <span className="material-icons-round text-sm">contacts</span>
                </div>
                <div>
                  <h2 className="font-semibold text-white text-lg">Contact Limits</h2>
                  <p className="text-[10px] font-medium text-white/90 uppercase tracking-wider mt-1">Resource allocation and capacity metrics</p>
                </div>
              </div>
              <button
                onClick={() => setShowLimitsModal(false)}
                className="w-8 h-8 hover:bg-white/10 text-white rounded-lg flex items-center justify-center transition-all"
              >
                <span className="material-icons-round text-base">close</span>
              </button>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">
              {(() => {
                const subscription = getCurrentSubscription();
                const limits = getContactLimits();

                if (!subscription) {
                  return (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-orange-100">
                        <span className="material-icons-round text-orange-500 text-2xl">warning_amber</span>
                      </div>
                      <h2 className="font-medium">No Active Plan</h2>
                      <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider leading-relaxed max-w-[240px] mx-auto">No active subscription detected. Please upgrade to access features.</p>
                    </div>
                  );
                }

                const pkg = subscription.package;
                return (
                  <div className="space-y-6">
                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 overflow-hidden relative">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/5 rounded-full blur-2xl -mr-16 -mt-16"></div>
                      <div className="relative z-10 flex items-center justify-between mb-6">
                        <div>
                          <span className="text-[9px] font-medium text-gray-600 uppercase tracking-wider mb-1 block">Current Plan</span>
                          <h2 className="font-medium">{pkg.name}</h2>
                        </div>
                        <div className="text-right">
                          <span className="text-[9px] font-medium text-gray-600 uppercase tracking-wider mb-1 block">Price</span>
                          <div className="text-lg font-normal text-bulky-blue-600 tabular-nums">₹{pkg.price}</div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h2 className="font-medium">
                        <span className="material-icons-round text-blue-500 text-sm">analytics</span>
                        Contact Usage Statistics
                      </h2>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                          <div className="text-sm font-normal text-bulky-navy-800 tabular-nums">{pkg.max_contacts}</div>
                          <div className="text-[9px] font-medium text-gray-600 uppercase tracking-wider">Capacity</div>
                        </div>
                        <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                          <div className="text-sm font-normal text-blue-600 tabular-nums">{limits.current}</div>
                          <div className="text-[9px] font-medium text-gray-600 uppercase tracking-wider">In Use</div>
                        </div>
                        <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                          <div className="text-sm font-normal text-green-600 tabular-nums">{pkg.max_contacts - limits.current}</div>
                          <div className="text-[9px] font-medium text-gray-600 uppercase tracking-wider">Available</div>
                        </div>
                      </div>

                      <div className="space-y-1.5 pt-2">
                        <div className="flex justify-between text-[8px] font-normal text-gray-400 uppercase tracking-wider">
                          <span>Usage Level</span>
                          <span>{Math.round((limits.current / pkg.max_contacts) * 100)}%</span>
                        </div>
                        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-bulky-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${(limits.current / pkg.max_contacts) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="p-6 border-t border-gray-100 shrink-0">
              <button
                onClick={() => setShowLimitsModal(false)}
                className="w-full bg-bulky-navy-800 hover:bg-bulky-navy-900 text-white py-3 rounded-xl font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {showErrorModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto transform animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="material-icons-round text-red-600 text-lg">error</span>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Error</h2>
                  <p className="text-sm text-gray-600">Action failed</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                {errorMessage}
              </p>
              
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    setErrorMessage('');
                  }}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Modal */}
      {showSuccessModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto transform animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="material-icons-round text-green-600 text-lg">check_circle</span>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Success</h2>
                  <p className="text-sm text-gray-600">Operation completed</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                {successMessage}
              </p>
              
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccessMessage('');
                  }}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Great!
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContactManagement;